import {
  cameraVisibilityPolygon,
  metersToPixels,
  planScrollBounds,
  type BoundingBox,
} from '@repo/front-end-utils';
import type { FloorPlannerLayout } from '@repo/frontend-types';
import { CONE_STYLES, type CanvasMarker } from '@repo/ui/planner';
import { placedCameraForFeed } from './observerSourceRanking';

/**
 * The Observers corner plan's frame on the camera view (`observer-corner-plan-camera-framing`, R1 to
 * R3). Pure: the stage passes `boxPx` to `ReadOnlyCanvas` as `fitBounds`, and draws `outlineM` as the
 * corner's first marker.
 */
export interface CornerCoverage {
  /** The placed camera's refid. */
  refid: string;
  /** What it sees, in floor metres: the cone Camera Feeds draws for it, walls occluding. */
  outlineM: Array<[number, number]>;
  /** The outline's bounds in world pixels, clipped to the floor's drawn extent. */
  boxPx: BoundingBox;
}

/**
 * Machines count by their centres, never their model footprints: those arrive after the models load,
 * and the canvas fits again on any new value of `fitBounds`, so a footprint arriving after the
 * operator zoomed would throw their view away (R4). A machine's half-width is all it can differ by.
 */
const BY_CENTRE = () => undefined;

/**
 * The coverage of the camera placed for `feedId`, on the floor the corner draws, or `null` (R3: the
 * whole floor is fitted and nothing is drawn) when the feed has no live camera on the plan, its camera
 * is on another floor, its outline is unusable (fewer than three points or a non-finite value), or
 * the outline's box does not overlap the floor's drawn extent.
 */
export const cornerCoverage = (
  layout: FloorPlannerLayout | null | undefined,
  feedId: number,
  floorIndex: number,
): CornerCoverage | null => {
  const placed = placedCameraForFeed(layout, feedId);
  if (!placed || placed.floorIndex !== floorIndex) return null;
  const { camera } = placed;
  // Walls absent: nothing occludes, and the floor's extent reads none.
  const design = { ...placed.design, walls: placed.design.walls ?? [] };
  const outline = cameraVisibilityPolygon(camera, design.walls);
  if (outline.length < 3 || outline.some(p => !Number.isFinite(p.x) || !Number.isFinite(p.y))) {
    return null;
  }
  const floor = planScrollBounds(design, {
    underlays: 'none',
    includeCameras: false,
    footprintOf: BY_CENTRE,
  });
  if (!floor) return null;
  const xs = outline.map(p => metersToPixels(p.x));
  const ys = outline.map(p => metersToPixels(p.y));
  const boxPx = {
    minX: Math.max(Math.min(...xs), floor.minX),
    maxX: Math.min(Math.max(...xs), floor.maxX),
    minY: Math.max(Math.min(...ys), floor.minY),
    maxY: Math.min(Math.max(...ys), floor.maxY),
  };
  if (!(boxPx.minX < boxPx.maxX && boxPx.minY < boxPx.maxY)) return null;
  return { refid: camera.refid, outlineM: outline.map(p => [p.x, p.y]), boxPx };
};

/**
 * The coverage as the corner draws it (R2): an outline in the selected-camera cone's edge colour
 * (card 24, `CONE_STYLES.selected`) at the marker layer's 2 px, with no fill — a fill would tint the
 * machines' status colours under it.
 */
export const coverageMarker = (coverage: CornerCoverage): CanvasMarker => ({
  id: `camera:${coverage.refid}`,
  color: CONE_STYLES.selected.stroke,
  shape: 'outline',
  polygonM: coverage.outlineM,
});
